/**
 * Shared report-request builder.
 *
 * Extracted from ReportsPage.handleGenerateReport so every entry point that
 * generates a report - the config popups, the report window's re-run dialog,
 * the Saved-configuration rail - turns a popup's thin selection params into
 * byte-identical backend `params`.
 */
import { capToMaxDate, snapToBusinessDay } from './reportDates';

/**
 * Build the backend `params` payload from a popup's thin selection params.
 *
 * @param {object} params - thin params from the popup (selection.params + date)
 * @param {object} ctx
 * @param {string} ctx.reportType
 * @param {number|string|null} [ctx.clientId] - page-context client id (fallback)
 * @returns {object} requestParams sent as ReportDataRequest.params
 */
export function buildReportRequestParams(params, { reportType, clientId = null } = {}) {
  // Cap every date at the latest lake date. Weekends and market holidays are NOT
  // moved for the as-of date or the range END: the server keeps the requested
  // label, carries values forward from the prior business day, and advances bond
  // accrued through the closed days. A range START still walks back, because
  // re-anchoring the opening value would change period returns and realized G/L.
  const rawRange = params.date_range || null;
  const requestParams = {
    date: capToMaxDate(params.date || params.date_range?.start),
    date_range: rawRange
      ? { ...rawRange, start: snapToBusinessDay(rawRange.start), end: capToMaxDate(rawRange.end) }
      : null,
    currency: params.currency || 'USD',
  };

  // Billing: the quarter rides through verbatim - its boundaries are calendar
  // facts, never snapped to business days (only `date` snaps).
  if (params.quarter) {
    requestParams.quarter = params.quarter;
  }

  // Popups may pass explicit resolution fields (group/client/account).
  if (params.group_codename) {
    requestParams.group_codename = params.group_codename.toUpperCase();
  }
  // @group runs sequentially (per-account); @@group runs as one aggregate.
  if (params.group_mode) {
    requestParams.group_mode = params.group_mode;
  }
  if (params.client_codename) {
    requestParams.client_codename = params.client_codename.toUpperCase();
  }
  if (params.account_number) {
    requestParams.account_number = String(params.account_number).replace(/^P/i, '');
  }
  if (params.client_name) {
    requestParams.client_name = params.client_name;
  }

  // Legacy portfolio + portfolioType path (when explicit fields not set).
  if (params.portfolio && !params.group_codename && !params.account_number && !params.client_name) {
    const portfolioClean = params.portfolio.replace('@', '').toUpperCase();

    if (params.portfolioType === 'group') {
      requestParams.group_codename = portfolioClean;
    } else if (params.portfolioType === 'account') {
      requestParams.account_number = portfolioClean.replace(/^P/, '');
      if (params.client_codename) {
        requestParams.client_codename = params.client_codename.toUpperCase();
      }
    } else if (!requestParams.client_codename) {
      requestParams.client_codename = portfolioClean;
    }

    if (params.specific_account && params.specific_account !== 'ALL') {
      requestParams.specific_account = params.specific_account;
    }
  } else if (params.portfolio && params.portfolioType === 'client' && !requestParams.client_codename) {
    requestParams.client_codename = params.portfolio.replace('@', '').toUpperCase();
  }

  if (params.specific_account && params.specific_account !== 'ALL') {
    requestParams.specific_account = params.specific_account;
  }

  // Reports opened from client context without a portfolio popup.
  if (
    !requestParams.group_codename
    && !requestParams.account_number
    && !requestParams.client_codename
  ) {
    requestParams.client_id = clientId || null;
    if (params.client_name) {
      requestParams.client_name = params.client_name;
    }
  }

  // Pluggable significance threshold (PM Q19) and fee exclusion (PM Q18).
  if (params.threshold_pct !== undefined && params.threshold_pct !== null) {
    requestParams.threshold_pct = params.threshold_pct;
  }
  if (params.exclude_fees !== undefined) {
    requestParams.exclude_fees = !!params.exclude_fees;
  }

  // Include report display options for Fixed Income Distribution
  if (reportType === 'fixed_income_distribution') {
    requestParams.show_summary_information = params.show_summary_information !== false;
    requestParams.show_distribution_by_moody_rating = params.show_distribution_by_moody_rating !== false;
    requestParams.show_distribution_by_sp_rating = params.show_distribution_by_sp_rating !== false;
    // Maturity defaults OFF (=== true), unlike the rating sections: APX prints
    // it only for select templates (e.g. KCRIMCO), so the section is opt-in.
    requestParams.show_distribution_by_maturity = params.show_distribution_by_maturity === true;
  }

  // Report-specific option passthroughs.
  if (params.cadence) requestParams.cadence = params.cadence;
  if (typeof params.net_of_fees === 'boolean') requestParams.net_of_fees = params.net_of_fees;
  // Security Cross Reference: the security filters ARE the report (the backend
  // filters holdings to this one CUSIP/ticker). Dropping them here silently
  // cross-references the wrong security over every holding in the selection.
  if (params.security_symbol) requestParams.security_symbol = params.security_symbol;
  if (params.security_type) requestParams.security_type = params.security_type;
  // show_individual_tax_lots is retired: reports always generate lumped and
  // the viewer expands lots per row. Legacy saved configs may still carry it
  // (the backend ignores it and the payload cache excludes it from the key).
  // FI Cash Projection: user-typed horizon (popup validates 1-120). Dropping
  // it here silently regenerated the default 12-month projection.
  if (params.projection_months !== undefined && params.projection_months !== null) {
    requestParams.projection_months = params.projection_months;
  }
  // FI Cash Projection: the par-redemption leg. The backend defaults it OFF (PM
  // ruling 2026-08-03, "default to interest only"), so only an explicit true
  // changes the report - but it is sent either way so the request states the
  // leg explicitly rather than relying on the backend default.
  if (typeof params.project_principal === 'boolean') {
    requestParams.project_principal = params.project_principal;
  }
  // Assets Under Management: the list's sort and filter controls. They are
  // applied server-side, so dropping one here silently regenerates the default
  // firm-wide list - a wrong report that still looks right. The accrued box is
  // display-only but must survive too, or a rerun silently loses the column.
  if (typeof params.include_accrued_interest === 'boolean') {
    requestParams.include_accrued_interest = params.include_accrued_interest;
  }
  if (params.sort_by) requestParams.sort_by = params.sort_by;
  if (params.sort_dir) requestParams.sort_dir = params.sort_dir;
  if (params.opened_from) requestParams.opened_from = params.opened_from;
  if (params.opened_to) requestParams.opened_to = params.opened_to;
  // An empty minimum is "no floor", not a floor of 0.
  if (params.min_total !== undefined && params.min_total !== null && params.min_total !== '') {
    requestParams.min_total = Number(params.min_total);
  }
  // Performance History: period granularity multi-select (monthly/quarterly/
  // yearly). The backend defaults to ['monthly'] when absent, so dropping the
  // selected set here would silently ignore the user's quarterly/yearly choice.
  if (Array.isArray(params.periods)) requestParams.periods = params.periods;
  // Performance History: period anchoring (from-date anniversaries vs calendar
  // month-ends). Backend defaults to 'from_date' (PM decision 2026-07-16); the
  // popup omits that default and sends period_anchor only for an explicit
  // 'calendar', so any present value must pass through to honour the choice.
  if (params.period_anchor) requestParams.period_anchor = params.period_anchor;
  // Performance Reconciliation: grouping variant (portfolio / security /
  // security_type_group / security_issuer / risk_country). Backend defaults to
  // security_type_group when absent.
  if (params.group_by) requestParams.group_by = params.group_by;
  // Money-weighted IRR method (six perf dialogs): Advent Average Capital Base
  // vs Discounted Cash Flow. Popups omit the default (average_capital), so any
  // present value is an explicit DCF choice the backend must see.
  if (params.irr_method) requestParams.irr_method = params.irr_method;
  // Performance History Extract: user-defined column selection (base + per-
  // asset-class TWR columns). Backend defaults to the base set when absent, so
  // dropping it here would silently ignore the picker.
  if (Array.isArray(params.columns)) requestParams.columns = params.columns;
  // Benchmark index selector (seven perf dialogs): the curated group ids the PM
  // chose to display. Absent => the report shows all benchmarks (back-compat);
  // present (even empty) restricts to the selection. ReportPopup reads this off
  // requestParams to filter the rendered index rows/columns, and reportPrefill
  // round-trips it so rerun/Recents/Saved restore the choice. Dropping it here
  // would silently disable the selector in prod - the filter would never see a
  // value even though the dialog set one.
  if (Array.isArray(params.selected_index_groups)) {
    requestParams.selected_index_groups = params.selected_index_groups;
  }

  // Printed-header inputs (never affect the Athena query).
  if (params.firm_name) requestParams.firm_name = params.firm_name;
  if (params.account_name) requestParams.account_name = params.account_name;
  if (params.account_code) requestParams.account_code = params.account_code;

  return requestParams;
}
